/**
 * Stats WebSocket Service
 *
 * Единый WebSocket для получения объединённой статистики (tickets + leads).
 * Использует SharedWebSocket — один реальный коннект на пользователя,
 * остальные вкладки получают данные через BroadcastChannel.
 */

import { SharedWebSocket } from '../utils/shared-websocket';
import authService from './auth.service';
import { onAuthTokenRefreshed } from '../utils/authTokenEmitter';

// Типы событий от бэкенда
type StatsEventType = 'connected' | 'unified_stats' | 'session_invalidated' | 'takeover' | 'error';

interface StatsMessage {
  event: StatsEventType;
  data?: Record<string, unknown>;
  detail?: string;
}

type StatsCallback = (data: Record<string, unknown>) => void;
type ConnectionCallback = (connected: boolean) => void;
type SessionEventCallback = (event: 'session_invalidated' | 'takeover', detail?: string) => void;

const LOG_PREFIX = '[StatsWebSocket]';
// Exponential backoff с cap 30s: 1 → 2 → 4 → 8 → 16 → 30 → 30 ...
const BACKOFF_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_RECONNECT_ATTEMPTS = 8;
const PING_INTERVAL_MS = 30_000;

function buildUrl(): string {
  const tokens = authService.getTokens();
  const token = tokens?.access_token ?? '';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/ws/stats?token=${encodeURIComponent(token)}`;
}

class StatsWebSocketService {
  private sharedWs: SharedWebSocket | null = null;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempt = 0;
  private generation = 0;
  private shouldReconnect = false;
  private currentTypes: string[] = [];
  private currentInterval = 15;

  private statsListeners = new Set<StatsCallback>();
  private connectionListeners = new Set<ConnectionCallback>();
  private sessionListeners = new Set<SessionEventCallback>();

  constructor() {
    onAuthTokenRefreshed(() => {
      if (this.sharedWs !== null) {
        console.log(LOG_PREFIX, 'JWT обновлён — пересоздаём соединение');
        this.reconnect();
      }
    });
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  onStats(cb: StatsCallback): () => void {
    this.statsListeners.add(cb);
    return () => this.statsListeners.delete(cb);
  }

  onConnectionChange(cb: ConnectionCallback): () => void {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  onSessionEvent(cb: SessionEventCallback): () => void {
    this.sessionListeners.add(cb);
    return () => this.sessionListeners.delete(cb);
  }

  connect(types: string[], interval = 15): void {
    this.currentTypes = types;
    this.currentInterval = interval;
    this.shouldReconnect = true;
    if (this.sharedWs !== null) {
      // already connected — just push new config
      this.sendCommand({ types, interval });
      return;
    }
    this.reconnectAttempt = 0;
    this._createSharedWs();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.generation++;
    this._clearPingInterval();
    this._clearReconnectTimer();
    if (this.sharedWs) {
      this.sharedWs.close();
      this.sharedWs = null;
    }
    this.notifyConnection(false);
  }

  reconnect(): void {
    this.generation++;
    this._clearPingInterval();
    this._clearReconnectTimer();
    if (this.sharedWs) {
      this.sharedWs.close();
      this.sharedWs = null;
    }
    this.reconnectAttempt = 0;
    this._createSharedWs();
  }

  updateTypes(types: string[]): void {
    this.currentTypes = types;
    this.sendCommand({ action: 'update_types', types });
  }

  updateInterval(interval: number): void {
    this.currentInterval = interval;
    this.sendCommand({ action: 'update_interval', interval });
  }

  isConnected(): boolean {
    return this.sharedWs !== null && this.sharedWs.readyState === WebSocket.OPEN;
  }

  // ─── Internal ───────────────────────────────────────────────────────────

  private _createSharedWs(): void {
    const myGen = ++this.generation;
    const url = buildUrl();
    console.log(LOG_PREFIX, 'Подключение к', url.replace(/token=[^&]+/, 'token=***'));

    let ws: SharedWebSocket;
    try {
      ws = new SharedWebSocket(url);
    } catch (err) {
      console.error(LOG_PREFIX, 'Ошибка создания WebSocket:', err);
      this._scheduleReconnect(myGen);
      return;
    }
    this.sharedWs = ws;

    ws.onopen = () => {
      if (this.generation !== myGen) return;
      console.log(LOG_PREFIX, 'Соединение установлено');
      this.reconnectAttempt = 0;
      this.sendCommand({ types: this.currentTypes, interval: this.currentInterval });
      this.notifyConnection(true);
      this._startPingInterval();
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.generation !== myGen) return;
      try {
        const msg = JSON.parse(event.data as string) as StatsMessage;
        this.handleMessage(msg);
      } catch (err) {
        console.error(LOG_PREFIX, 'Ошибка парсинга сообщения:', err);
      }
    };

    ws.onerror = (event: Event) => {
      if (this.generation !== myGen) return;
      console.error(LOG_PREFIX, 'Ошибка соединения:', event);
    };

    ws.onclose = (event: CloseEvent) => {
      if (this.generation !== myGen) return;
      console.warn(LOG_PREFIX, 'Соединение закрыто:', { code: event.code, reason: event.reason });
      this._clearPingInterval();
      this.notifyConnection(false);

      // 1008 = policy violation (invalid/expired token) — let AuthContext handle re-login.
      if (event.code === 1008 || event.code === 4001) return;
      if (this.shouldReconnect) this._scheduleReconnect(myGen);
    };
  }

  private _startPingInterval(): void {
    this._clearPingInterval();
    this.pingIntervalId = setInterval(() => {
      if (this.sharedWs) {
        try {
          this.sharedWs.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // connection might have dropped — onclose will handle
        }
      }
    }, PING_INTERVAL_MS);
  }

  private _clearPingInterval(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  private handleMessage(msg: StatsMessage): void {
    switch (msg.event) {
      case 'connected':
        console.log(LOG_PREFIX, 'Подтверждение подключения от сервера');
        break;

      case 'unified_stats':
        if (msg.data) {
          this.statsListeners.forEach(cb => {
            try { cb(msg.data!); } catch (err) { console.error(LOG_PREFIX, 'Ошибка в stats listener:', err); }
          });
        }
        break;

      case 'session_invalidated':
        console.warn(LOG_PREFIX, 'Сессия инвалидирована');
        this.shouldReconnect = false;
        this.sessionListeners.forEach(cb => {
          try { cb('session_invalidated', msg.detail); } catch { /* ignore */ }
        });
        if (this.sharedWs) {
          this.sharedWs.close();
          this.sharedWs = null;
        }
        break;

      case 'takeover':
        console.info(LOG_PREFIX, 'Takeover — другой клиент перехватил соединение');
        this.shouldReconnect = false;
        this.sessionListeners.forEach(cb => {
          try { cb('takeover', msg.detail); } catch { /* ignore */ }
        });
        break;

      case 'error':
        console.error(LOG_PREFIX, 'Ошибка от сервера:', msg.detail);
        break;

      default:
        console.log(LOG_PREFIX, 'Неизвестное событие:', msg);
    }
  }

  private sendCommand(cmd: Record<string, unknown>): void {
    if (this.sharedWs) {
      try {
        this.sharedWs.send(JSON.stringify(cmd));
      } catch {
        // connection might have dropped — onclose will handle it
      }
    }
  }

  private _scheduleReconnect(gen: number): void {
    this._clearReconnectTimer();

    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.warn(
        LOG_PREFIX,
        `Превышен лимит переподключений (${MAX_RECONNECT_ATTEMPTS}). Остановка. Обновите страницу для повторной попытки.`,
      );
      this.shouldReconnect = false;
      this.sharedWs = null;
      return;
    }

    const delay = BACKOFF_DELAYS_MS[this.reconnectAttempt] ?? BACKOFF_DELAYS_MS[BACKOFF_DELAYS_MS.length - 1];
    this.reconnectAttempt++;
    console.log(LOG_PREFIX, `Переподключение через ${delay}мс (попытка ${this.reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS})`);

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      if (this.generation !== gen) return;
      if (!this.shouldReconnect) return;
      this._createSharedWs();
    }, delay);
  }

  private _clearReconnectTimer(): void {
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  private notifyConnection(connected: boolean): void {
    this.connectionListeners.forEach(cb => {
      try { cb(connected); } catch { /* ignore */ }
    });
  }
}

const statsWebSocketService = new StatsWebSocketService();
export default statsWebSocketService;
